import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Person as PersonIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  ArrowBack as ArrowBackIcon,
  ViewList as ViewListIcon,
  Summarize as SummarizeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { saidaService, SaidaDetalhes, ProdutoSaida, ProdutoSaidaResumido } from '../../services/saida';

interface SaidaVisualizarProps {
  id: number;
}

type ViewMode = 'normal' | 'resumo';

const SaidaVisualizar: React.FC<SaidaVisualizarProps> = ({ id }) => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>('resumo');

  const { data: notaSaida, isLoading, error } = useQuery<SaidaDetalhes>({
    queryKey: ['saida', id],
    queryFn: () => saidaService.getById(id),
    enabled: !!id,
  });

  const formatCurrency = (value: string | number) => {
    const numericValue = typeof value === 'string' ? parseFloat(value.replace(',', '.')) : value;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(isNaN(numericValue) ? 0 : numericValue);
  };

  const formatCodigoBarras = (value: string | string[] | undefined) => {
    if (!value) return '-';
    if (Array.isArray(value)) return value.join(', ');
    return value;
  };

  const formatSemComissao = (value: number | boolean) => {
    return value === 1 || value === true ? 'SIM' : 'NÃO';
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  if (error || !notaSaida) {
    const errorMessage = (error as any)?.response?.data?.title
      || (error as any)?.response?.data?.message
      || (error as any)?.message
      || 'Erro ao carregar a nota de saída.';
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, flexDirection: 'column', alignItems: 'center' }}>
        <Typography color="error" gutterBottom>Erro ao carregar a nota de saída.</Typography>
        <Typography color="error" variant="body2">{errorMessage}</Typography>
      </Box>
    );
  }

  const produtos = viewMode === 'normal'
    ? (notaSaida.produtos || [])
    : (notaSaida.produtosResumidos || []);
  const produtosOrdenados = [...produtos].sort((a, b) =>
    (a.referencia || '').localeCompare(b.referencia || '', 'pt-BR', { numeric: true, sensitivity: 'base' })
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header */}
      <Paper sx={{ p: 3, backgroundColor: 'primary.main', color: 'white' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
              Saída #{notaSaida.id}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Vendedor(a): {notaSaida.vendedor || notaSaida.nomeVendedor || '-'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label="Visualização"
              color="secondary"
              variant="outlined"
              sx={{
                borderColor: 'white',
                color: 'white',
                '& .MuiChip-label': { color: 'white' },
              }}
            />
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, value) => value && setViewMode(value)}
              size="small"
              sx={{
                backgroundColor: 'white',
                '& .MuiToggleButton-root': {
                  color: 'primary.main',
                  '&.Mui-selected': {
                    backgroundColor: 'primary.light',
                    color: 'white',
                  },
                },
              }}
            >
              <ToggleButton value="normal">
                <ViewListIcon sx={{ mr: 0.5, fontSize: 18 }} />
                Normal
              </ToggleButton>
              <ToggleButton value="resumo">
                <SummarizeIcon sx={{ mr: 0.5, fontSize: 18 }} />
                Resumo
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        {/* Dados Principais */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Dados da Saída
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Código</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>#{notaSaida.id}</Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">Promotor de Vendas</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {notaSaida.nomePromotor || '-'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">Vendedor(a)</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {notaSaida.nomeVendedor || notaSaida.vendedor || '-'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">CPF</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {notaSaida.cpf || '-'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">Telefone</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {notaSaida.telefone || '-'}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Praça e Datas */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              <LocationIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Praça e Datas
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Praça</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {notaSaida.praca || '-'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">Endereço</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {notaSaida.endereco || '-'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">Data de Saída</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {notaSaida.dataSaidaString || notaSaida.dataSaida || '-'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">Data de Retorno</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {notaSaida.dataPrevistaRetornoString || notaSaida.dataPrevistaRetorno || '-'}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Produtos */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Produtos da Saída
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {viewMode === 'normal' ? (
                    <>
                      <TableCell sx={{ fontWeight: 'bold' }}>Código de Barras</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Referência</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Produto</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Preço de Venda</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell sx={{ fontWeight: 'bold' }}>Referência</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Produto</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Preço de Venda</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Quantidade</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Sem Comissão</TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {produtosOrdenados.length > 0 ? (
                  produtosOrdenados.map((produto, index) => (
                    <TableRow key={`${produto.id}-${index}`}>
                      {viewMode === 'normal' ? (
                        <>
                          <TableCell>{formatCodigoBarras((produto as ProdutoSaida).codigoBarras)}</TableCell>
                          <TableCell>{produto.referencia}</TableCell>
                          <TableCell>{produto.nomeProduto}</TableCell>
                          <TableCell>{formatCurrency(produto.precoVenda)}</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>{produto.referencia}</TableCell>
                          <TableCell>{produto.nomeProduto}</TableCell>
                          <TableCell>{formatCurrency(produto.precoVenda)}</TableCell>
                          <TableCell>{produto.quantidade}</TableCell>
                          <TableCell>{formatSemComissao((produto as ProdutoSaidaResumido).semComissao)}</TableCell>
                        </>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={viewMode === 'normal' ? 4 : 5} align="center">
                      <Typography sx={{ py: 4, color: 'text.secondary' }}>
                        Nenhum produto encontrado nesta saída.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Botão Voltar */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/saidas')}
          sx={{ minWidth: 150 }}
        >
          Voltar para Lista
        </Button>
      </Box>
    </Box>
  );
};

export default SaidaVisualizar;
